import React, { useState, useRef, useEffect } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../config/firebaseConfig';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Play, Pause, Volume2, VolumeX, 
  Bookmark, FileText, Maximize, Minimize,
  FastForward, Rewind, Award, Check,
  X, AlertTriangle, ChevronRight,
  Clock, Download, Plus
} from 'react-feather';
import { useUserProfile } from '../../contexts/UserProfileContext';
import PoolQuiz from '../../components/PoolQuiz/PoolQuiz';
import { QUIZ_SOURCE } from '../../utils/questionPool';
import styles from './EducationModule.module.css';

const EducationModule = ({ 
  show, 
  onHide, 
  sopData, 
  onComplete 
}) => {
  const { employeeData, employeeId } = useUserProfile();

  // Video player state
  const videoRef = useRef(null);
  const progressRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hoverTime, setHoverTime] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [isVideoFormat, setIsVideoFormat] = useState(true);
  
  // Learning state
  const [activeTab, setActiveTab] = useState('content');
  const [progress, setProgress] = useState(0);
  const [lastPosition, setLastPosition] = useState(0);
  const [notes, setNotes] = useState('');
  const [bookmarks, setBookmarks] = useState([]);
  const [activeSection, setActiveSection] = useState(0);
  const [quizStarted, setQuizStarted] = useState(false);
  const [certificateEarned, setCertificateEarned] = useState(false);

  // Video sections/chapters for the timeline
  const videoSections = [
    { title: "Introduction", startTime: 0, endTime: 60 },
    { title: "Core Concepts", startTime: 61, endTime: 180 },
    { title: "Practical Application", startTime: 181, endTime: 300 },
    { title: "Summary", startTime: 301, endTime: 360 }
  ];

  // Sınav soruları departman soru havuzundan gelir (bkz. components/PoolQuiz)

  // Load saved progress on component mount
  useEffect(() => {
    const loadProgress = async () => {
      if (!auth.currentUser || !sopData) return;

      try {
        const progressRef = doc(db, 'userProgress', `${auth.currentUser.uid}_${sopData.id}`);
        const progressDoc = await getDoc(progressRef);
        
        if (progressDoc.exists()) {
          const data = progressDoc.data();
          setLastPosition(data.lastPosition || 0);
          setProgress(data.progress || 0);
          setNotes(data.notes || '');
          setBookmarks(data.bookmarks || []);
          setCertificateEarned(data.certificateEarned || false);
        }
      } catch (error) {
        console.error("Error loading progress:", error);
      }
    };

    loadProgress();
  }, [sopData]);

  // Check if the file is a video when component mounts
  useEffect(() => {
    if (sopData?.fileUrl) {
      // Check if the file is a video based on fileType in sopData or file extension
      const isVideo = 
        (sopData.fileType && sopData.fileType.includes('video')) || 
        sopData.fileUrl.toLowerCase().endsWith('.mp4');
      
      setIsVideoFormat(isVideo);
    }
  }, [sopData]);

  // Video event handlers
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      
      // Update progress percentage
      const progressPercent = Math.floor((video.currentTime / video.duration) * 100);
      setProgress(Math.max(progress, progressPercent));
      
      // Update active section
      const currentSection = videoSections.findIndex(
        section => video.currentTime >= section.startTime && video.currentTime <= section.endTime
      );
      
      if (currentSection !== -1) {
        setActiveSection(currentSection);
      }
    };

    const onLoadedMetadata = () => {
      setDuration(video.duration);
      
      // Set video to last watched position
      if (lastPosition > 0) {
        video.currentTime = lastPosition;
      }
    };

    const onEnded = () => {
      setIsPlaying(false);
      setProgress(100);
      saveProgress(100, video.currentTime);
    };
    
    const onError = () => {
      setVideoError(true);
      console.error("Error loading video");
    };

    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('ended', onEnded);
    video.addEventListener('error', onError);

    return () => {
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('error', onError);
    };
  }, [videoRef, lastPosition, progress]);

  // Handle progress bar hover
  useEffect(() => {
    const progressBar = progressRef.current;
    if (!progressBar) return;

    const handleMouseMove = (e) => {
      const rect = progressBar.getBoundingClientRect();
      const position = (e.clientX - rect.left) / rect.width;
      const time = position * duration;
      setHoverTime(time);
    };

    const handleMouseLeave = () => {
      setHoverTime(null);
    };

    progressBar.addEventListener('mousemove', handleMouseMove);
    progressBar.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      progressBar.removeEventListener('mousemove', handleMouseMove);
      progressBar.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [progressRef, duration]);

  // Save progress to Firestore
  const saveProgress = async (progressValue, position) => {
    if (!auth.currentUser || !sopData) return;

    try {
      const progressRef = doc(db, 'userProgress', `${auth.currentUser.uid}_${sopData.id}`);
      await setDoc(progressRef, {
        userId: auth.currentUser.uid,
        sopId: sopData.id,
        sopName: sopData.name,
        progress: progressValue,
        lastPosition: position,
        notes,
        bookmarks,
        certificateEarned,
        lastUpdated: serverTimestamp()
      }, { merge: true });
    } catch (error) {
      console.error("Error saving progress:", error);
    }
  };

  // Format time in seconds to mm:ss
  const formatTime = (timeInSeconds) => {
    if (isNaN(timeInSeconds)) return "0:00";
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  // Video player controls
  const togglePlay = () => {
    if (!videoRef.current) return;
    
    if (isPlaying) {
      videoRef.current.pause();
      saveProgress(progress, videoRef.current.currentTime);
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
    }
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    
    if (isMuted) {
      videoRef.current.volume = volume;
    } else {
      videoRef.current.volume = 0;
    }
    setIsMuted(!isMuted);
  };

  const seek = (time) => {
    if (!videoRef.current) return;
    
    videoRef.current.currentTime = time;
    setCurrentTime(time);
  };

  const handleProgressClick = (e) => {
    if (!progressRef.current || !videoRef.current) return;
    
    const rect = progressRef.current.getBoundingClientRect();
    const position = (e.clientX - rect.left) / rect.width;
    const time = position * duration;
    seek(time);
  };

  const skipForward = () => {
    if (!videoRef.current) return;
    videoRef.current.currentTime += 10;
  };

  const skipBackward = () => {
    if (!videoRef.current) return;
    videoRef.current.currentTime -= 10;
  };

  const toggleFullscreen = () => {
    const videoContainer = document.querySelector(`.${styles.videoWrapper}`);
    if (!videoContainer) return;

    if (!document.fullscreenElement) {
      videoContainer.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch(err => {
        console.error('Failed to enter fullscreen mode:', err);
      });
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().then(() => {
          setIsFullscreen(false);
        }).catch(err => {
          console.error('Failed to exit fullscreen mode:', err);
        });
      }
    }
  };

  // Bookmark functions
  const addBookmark = () => {
    if (!videoRef.current) return;
    
    const newBookmark = {
      id: Date.now(),
      time: videoRef.current.currentTime,
      label: `${formatTime(videoRef.current.currentTime)} - Bookmark`
    };
    
    const updatedBookmarks = [...bookmarks, newBookmark];
    setBookmarks(updatedBookmarks);
    saveProgress(progress, videoRef.current.currentTime);
    toast.success("Bookmark added");
  };

  const goToBookmark = (time) => {
    seek(time);
  };

  // Notes functions
  const handleNotesChange = (e) => {
    setNotes(e.target.value);
  };

  const saveNotes = () => {
    if (!videoRef.current) return;
    saveProgress(progress, videoRef.current.currentTime);
    toast.success("Notes saved");
  };

  // Quiz functions
  const startQuiz = () => {
    setQuizStarted(true);
  };

  // Havuz sınavı geçildi (veya havuz boş olduğu için atlandı)
  const handleQuizPassed = () => {
    setQuizStarted(false);
    setCertificateEarned(true);
    saveProgress(100, videoRef.current ? videoRef.current.currentTime : 0);
  };

  const handleComplete = () => {
    if (onComplete) {
      onComplete(sopData.id);
    }
    onHide();
  };

  // Animation variants
  const modalVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } },
    exit: { opacity: 0, scale: 0.98, transition: { duration: 0.2 } }
  };

  const contentVariants = {
    hidden: { opacity: 0, x: 10 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.3 } }
  };

  return (
    <div className={`${styles.modalOverlay} ${show ? styles.show : ''}`} onClick={onHide}>
      <AnimatePresence>
        {show && (
          <motion.div
            className={styles.modal}
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={e => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <div className={styles.headerLeft}>
                <div className={styles.sopBadge}>SOP</div>
                <div className={styles.sopInfo}>
                  <h2 className={styles.sopTitle}>{sopData?.name}</h2>
                  <div className={styles.sopMeta}>
                    <span className={styles.version}>v{sopData?.versionNumber}</span>
                    <span className={styles.duration}>
                      <Clock size={14} />
                      {Math.floor(duration / 60)}m {Math.floor(duration % 60)}s
                    </span>
                  </div>
                </div>
              </div>
              
              <button 
                className={styles.closeButton} 
                onClick={onHide}
                aria-label="Close"
              >
                <X size={24} />
              </button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.learningContainer}>
                <div className={styles.videoSection}>
                  <div className={styles.videoWrapper}>
                    {isVideoFormat ? (
                      <>
                        <video
                          ref={videoRef}
                          className={styles.videoPlayer}
                          onClick={togglePlay}
                        >
                          <source src={sopData?.fileUrl} type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                        
                        {videoError && (
                          <div className={styles.videoError}>
                            <AlertTriangle size={40} />
                            <p>Failed to load video. Please try again later.</p>
                          </div>
                        )}
                        
                        {!isPlaying && !videoError && (
                          <div className={styles.playOverlay} onClick={togglePlay}>
                            <motion.div 
                              className={styles.playButton}
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <Play size={32} />
                            </motion.div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className={styles.nonVideoContent}>
                        <FileText size={48} />
                        <p>This SOP is not in video format. Please use the download button to view the document.</p>
                        <a 
                          href={sopData?.fileUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className={styles.downloadDocButton}
                        >
                          <Download size={16} /> View Document
                        </a>
                      </div>
                    )}
                  </div>
                  
                  {isVideoFormat && !videoError && (
                    <>
                      <div className={styles.videoControls}>
                        <div 
                          className={styles.progressBarContainer}
                          ref={progressRef}
                          onClick={handleProgressClick}
                        >
                          <div className={styles.sectionsOverlay}>
                            {videoSections.map((section, index) => (
                              <div 
                                key={index}
                                className={`${styles.sectionMarker} ${activeSection === index ? styles.activeSection : ''}`}
                                style={{ 
                                  left: `${(section.startTime / duration) * 100}%`, 
                                  width: `${((section.endTime - section.startTime) / duration) * 100}%` 
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  seek(section.startTime);
                                }}
                              >
                                <div className={styles.sectionTooltip}>
                                  {section.title}
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          <div className={styles.progressBackground}></div>
                          <div 
                            className={styles.progressFill}
                            style={{ width: `${(currentTime / duration) * 100}%` }}
                          ></div>
                          
                          <div 
                            className={styles.progressHandle}
                            style={{ left: `${(currentTime / duration) * 100}%` }}
                          ></div>
                          
                          {hoverTime !== null && (
                            <div 
                              className={styles.timeTooltip}
                              style={{ left: `${(hoverTime / duration) * 100}%` }}
                            >
                              {formatTime(hoverTime)}
                            </div>
                          )}
                        </div>
                        
                        <div className={styles.controlsRow}>
                          <div className={styles.controlsLeft}>
                            <button 
                              className={styles.controlButton} 
                              onClick={togglePlay}
                              aria-label={isPlaying ? "Pause" : "Play"}
                            >
                              {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                            </button>
                            
                            <button 
                              className={styles.controlButton} 
                              onClick={skipBackward}
                              aria-label="10 seconds backward"
                            >
                              <Rewind size={18} />
                            </button>
                            
                            <button 
                              className={styles.controlButton} 
                              onClick={skipForward}
                              aria-label="10 seconds forward"
                            >
                              <FastForward size={18} />
                            </button>
                            
                            <div className={styles.volumeControl}>
                              <button 
                                className={styles.controlButton} 
                                onClick={toggleMute}
                                aria-label={isMuted ? "Unmute" : "Mute"}
                              >
                                {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                              </button>
                              <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.05"
                                value={isMuted ? 0 : volume}
                                onChange={handleVolumeChange}
                                className={styles.volumeSlider}
                                aria-label="Volume"
                              />
                            </div>
                            
                            <div className={styles.timeDisplay}>
                              {formatTime(currentTime)} / {formatTime(duration)}
                            </div>
                          </div>
                          
                          <div className={styles.controlsRight}>
                            <button 
                              className={styles.controlButton} 
                              onClick={addBookmark}
                              aria-label="Add bookmark"
                            >
                              <Bookmark size={18} />
                            </button>
                            
                            <button 
                              className={styles.controlButton} 
                              onClick={toggleFullscreen}
                              aria-label="Toggle fullscreen"
                            >
                              {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      <div className={styles.progressStats}>
                        <div className={styles.currentSectionTitle}>
                          <h3>
                            <span>Current Section:</span> 
                            {videoSections[activeSection]?.title || "Introduction"}
                          </h3>
                        </div>
                        
                        <div className={styles.progressWrapper}>
                          <div className={styles.progressLabel}>
                            <span>Learning Progress:</span>
                            <span className={styles.progressPercentage}>{progress}%</span>
                          </div>
                          
                          <div className={styles.progressBarOuter}>
                            <div 
                              className={styles.progressBarInner} 
                              style={{ width: `${progress}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                
                <div className={styles.sidePanel}>
                  <div className={styles.tabsContainer}>
                    <button 
                      className={`${styles.tabButton} ${activeTab === 'content' ? styles.activeTab : ''}`}
                      onClick={() => setActiveTab('content')}
                    >
                      Content
                    </button>
                    <button 
                      className={`${styles.tabButton} ${activeTab === 'notes' ? styles.activeTab : ''}`}
                      onClick={() => setActiveTab('notes')}
                    >
                      Notes
                    </button>
                    <button 
                      className={`${styles.tabButton} ${activeTab === 'bookmarks' ? styles.activeTab : ''}`}
                      onClick={() => setActiveTab('bookmarks')}
                    >
                      Bookmarks
                    </button>
                  </div>
                  
                  <div className={styles.tabContent}>
                    <AnimatePresence mode="wait">
                      {activeTab === 'content' && (
                        <motion.div 
                          key="content"
                          variants={contentVariants}
                          initial="hidden"
                          animate="visible"
                          exit="hidden"
                          className={styles.contentTab}
                        >
                          <h4 className={styles.sectionTitle}>Video Sections</h4>
                          <ul className={styles.sectionsList}>
                            {videoSections.map((section, index) => (
                              <li 
                                key={index} 
                                className={`${styles.sectionItem} ${activeSection === index ? styles.activeSectionItem : ''}`}
                                onClick={() => seek(section.startTime)}
                              >
                                <div className={styles.sectionItemContent}>
                                  <div className={styles.sectionBullet}></div>
                                  <div className={styles.sectionInfo}>
                                    <span className={styles.sectionItemTitle}>{section.title}</span>
                                    <span className={styles.sectionDuration}>
                                      {formatTime(section.startTime)} - {formatTime(section.endTime)}
                                    </span>
                                  </div>
                                  <ChevronRight size={16} className={styles.sectionArrow} />
                                </div>
                                {activeSection === index && (
                                  <div className={styles.progressTracker}>
                                    <div 
                                      className={styles.sectionProgress}
                                      style={{ 
                                        width: `${Math.min(100, Math.max(0, ((currentTime - section.startTime) / (section.endTime - section.startTime)) * 100))}%` 
                                      }}
                                    ></div>
                                  </div>
                                )}
                              </li>
                            ))}
                          </ul>
                          
                          {progress >= 80 && !quizStarted && !certificateEarned && (
                            <div className={styles.quizPrompt}>
                              <h4>Knowledge Test</h4>
                              <p>
                                You've watched over 80% of this training. Complete a short quiz to earn your certificate.
                              </p>
                              <button 
                                className={styles.startQuizButton}
                                onClick={startQuiz}
                              >
                                Start Quiz
                              </button>
                            </div>
                          )}
                          
                          {certificateEarned && (
                            <div className={styles.certificateSection}>
                              <div className={styles.certificateIcon}>
                                <Award size={40} />
                              </div>
                              <h4>Certificate Earned!</h4>
                              <p>
                                You have successfully completed this training module.
                              </p>
                              <button className={styles.downloadCertificateButton}>
                                <Download size={14} /> Download Certificate
                              </button>
                            </div>
                          )}
                        </motion.div>
                      )}
                      
                      {activeTab === 'notes' && (
                        <motion.div 
                          key="notes"
                          variants={contentVariants}
                          initial="hidden"
                          animate="visible"
                          exit="hidden"
                          className={styles.notesTab}
                        >
                          <div className={styles.notesHeader}>
                            <h4>Your Notes</h4>
                            <span className={styles.notesSaved}>Auto-saves while typing</span>
                          </div>
                          <textarea
                            value={notes}
                            onChange={handleNotesChange}
                            onBlur={saveNotes}
                            placeholder="Take notes while watching..."
                            className={styles.notesTextarea}
                          />
                          <button 
                            className={styles.saveNotesButton}
                            onClick={saveNotes}
                          >
                            Save Notes
                          </button>
                        </motion.div>
                      )}
                      
                      {activeTab === 'bookmarks' && (
                        <motion.div 
                          key="bookmarks"
                          variants={contentVariants}
                          initial="hidden"
                          animate="visible"
                          exit="hidden"
                          className={styles.bookmarksTab}
                        >
                          <div className={styles.bookmarksHeader}>
                            <h4>Your Bookmarks</h4>
                            <button 
                              className={styles.addBookmarkButton}
                              onClick={addBookmark}
                            >
                              <Plus size={16} /> Add
                            </button>
                          </div>
                          
                          {bookmarks.length > 0 ? (
                            <ul className={styles.bookmarksList}>
                              {bookmarks.map((bookmark) => (
                                <li 
                                  key={bookmark.id} 
                                  className={styles.bookmarkItem}
                                  onClick={() => goToBookmark(bookmark.time)}
                                >
                                  <Bookmark size={16} className={styles.bookmarkIcon} />
                                  <span className={styles.bookmarkLabel}>{bookmark.label}</span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <div className={styles.emptyState}>
                              <p>No bookmarks yet. Add bookmarks to quickly return to important moments.</p>
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </div>
            </div>
            
            <div className={styles.modalFooter}>
              <button 
                className={styles.secondaryButton} 
                onClick={onHide}
              >
                Close
              </button>
              
              {progress >= 100 && !certificateEarned && (
                <button 
                  className={styles.primaryButton}
                  onClick={startQuiz}
                >
                  Take Quiz to Complete
                </button>
              )}
              
              {certificateEarned && (
                <button 
                  className={styles.successButton}
                  onClick={handleComplete}
                >
                  <Check size={16} /> Mark as Completed
                </button>
              )}
            </div>
            
            {/* Okuma sonu sınavı: departman soru havuzundan rastgele sorular */}
            <PoolQuiz
              show={quizStarted && !certificateEarned}
              group={employeeData?.role}
              source={{ type: QUIZ_SOURCE.SOP, id: sopData?.id || '', name: sopData?.name || '' }}
              user={{
                uid: auth.currentUser?.uid,
                employeeId,
                name: `${employeeData?.eName || ''} ${employeeData?.eSurname || ''}`.trim(),
              }}
              onPassed={handleQuizPassed}
              onClose={() => setQuizStarted(false)}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EducationModule;